import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Chip, Stack, Divider } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BadgeIcon from '@mui/icons-material/Badge';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SchoolIcon from '@mui/icons-material/School';
import { COLORS } from '../ViewPatientDialog.utils';

export default function ClientAccountTab({ patient }) {
  const client = patient?.client;

  if (!client) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: COLORS.muted }}>Aucune information de compte client disponible.</Typography>
      </Box>
    );
  }

  const getStatusChip = (status) => {
    const isActive = status === 'active';
    return (
      <Chip
        label={isActive ? 'Actif' : status || 'Inconnu'}
        size="small"
        sx={{
          backgroundColor: isActive ? COLORS.successBg : COLORS.warningBg,
          color: isActive ? COLORS.success : COLORS.warning,
          fontWeight: 700,
          textTransform: 'capitalize',
        }}
      />
    );
  };

  const getYesNoChip = (val) => {
    const isYes = val === 1 || val === true;
    return (
      <Chip
        label={isYes ? 'Oui' : 'Non'}
        size="small"
        sx={{
          backgroundColor: isYes ? COLORS.successBg : COLORS.mutedBg || '#EDF2F7',
          color: isYes ? COLORS.success : COLORS.muted,
          fontWeight: 700,
        }}
      />
    );
  };

  return (
    <Box sx={{ pb: 3 }}>
      <Grid container spacing={3}>
        {/* Informations générales */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: COLORS.text, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ color: COLORS.primary }} /> Identité du Client
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: COLORS.muted, fontWeight: 600 }}>ID Client</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>#{client.id_client || '—'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: COLORS.muted, fontWeight: 600 }}>Nom complet</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{[client.prenom_client, client.nom_client].filter(Boolean).join(' ') || '—'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: COLORS.muted, fontWeight: 600 }}>Pseudo</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{client.pseudo_client || 'Non renseigné'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: COLORS.muted, fontWeight: 600 }}>État du compte</Typography>
                  {getStatusChip(client.etat_client)}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: COLORS.muted, fontWeight: 600 }}>Est un patient</Typography>
                  {getYesNoChip(client.is_patient)}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: COLORS.muted, fontWeight: 600 }}>Email vérifié</Typography>
                  {getYesNoChip(client.email_verified)}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Coordonnées */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: COLORS.text, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon sx={{ color: COLORS.primary }} /> Coordonnées & Contact
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: COLORS.muted, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}><EmailIcon fontSize="small" /> Email</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{client.email_client || '—'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: COLORS.muted, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}><PhoneIcon fontSize="small" /> Téléphone</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{client.phone_client || '—'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: COLORS.muted, fontWeight: 600 }}>Adresse</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{client.addresse_client || '—'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: COLORS.muted, fontWeight: 600 }}>Ville</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{client.ville_client || '—'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: COLORS.muted, fontWeight: 600 }}>Code Postal</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{client.cp_client || '—'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: COLORS.muted, fontWeight: 600 }}>Province / Région</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{client.province_client || '—'}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistiques, Activité & Abonnement */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: COLORS.text, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BadgeIcon sx={{ color: COLORS.primary }} /> Activité, Inscription & Abonnement
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 700, textTransform: 'uppercase' }}>Date d'inscription</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarTodayIcon fontSize="small" sx={{ color: COLORS.primary }} /> {client.date_inscrip_client || '—'}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 700, textTransform: 'uppercase' }}>Dernière connexion</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{client.last_cnx_client || 'Jamais connecté'}</Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 700, textTransform: 'uppercase' }}>État de l'abonnement</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{client.etat_abn || 'Aucun abonnement'}</Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: COLORS.primarySoft || '#F7F5FF', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <VideocamIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 700 }}>Nombre de vidéos</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: COLORS.text }}>{client.nbr_video ?? '0'}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: COLORS.primarySoft || '#F7F5FF', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocalOfferIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 700 }}>Nombre d'offres</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: COLORS.text }}>{client.nbr_offre ?? '0'}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: COLORS.primarySoft || '#F7F5FF', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SchoolIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 700 }}>Nombre d'ateliers (Workshops)</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: COLORS.text }}>{client.nbr_workshops ?? '0'}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}